import { useCallback, useEffect, useRef, useState } from 'react'
import { palettePrimaryMain } from '@/theme/designTokens'

import {
  Joyride,
  ACTIONS,
  EVENTS,
  STATUS,
  type EventHandler,
  type Step,
  type StepTarget,
} from 'react-joyride'
import { usePatchSensemakerFtux } from '@/features/auth/api/useAuth'
import { sensemakerFtuxJoyrideStyles } from '@/features/dashboard/lib/ftux-joyride-theme'

/**
 * Vertical gap between spotlight (target) and tooltip — Figma Tour 7 (`4154:4475`): card bottom y=423,
 * tooltip top y=440 ⇒ **17px**. Requires `hideArrow` so floating-ui offset is not increased by `arrowSize`.
 */
const FTUX_STEP_TARGET_GAP_PX = 17

/** Joyride’s target-position hook debounces scroll/resize by 100ms — wait past that before nudging. */
const SPOTLIGHT_REMEASURE_DELAY_MS = 120

type SensemakerFtuxTourProps = Readonly<{
  steps: Step[]
  /** API returned PENDING — tour may run when dashboard is ready */
  shouldRun: boolean
}>

function resolveStepTarget(target: StepTarget | undefined): Element | null {
  if (target == null) return null
  if (typeof target === 'string') {
    try {
      return document.querySelector(target)
    } catch {
      return null
    }
  }
  if (typeof target === 'function') {
    try {
      return target()
    } catch {
      return null
    }
  }
  if (typeof target === 'object' && 'current' in target) {
    return target.current
  }
  return target
}

/**
 * Force Joyride’s `useTargetPosition` listeners to re-read `getBoundingClientRect` after
 * scroll/layout settle — fixes intermittent spotlight drift on AppShell’s nested scroller.
 */
function nudgeSpotlightRemeasure(): void {
  globalThis.dispatchEvent(new Event('resize'))
}

function runAfterDoubleAnimationFrame(callback: () => void): void {
  globalThis.requestAnimationFrame(() => {
    globalThis.requestAnimationFrame(callback)
  })
}

function scheduleDelayedSpotlightRemeasure(delayMs: number): ReturnType<typeof globalThis.setTimeout> {
  return globalThis.setTimeout(() => {
    nudgeSpotlightRemeasure()
    runAfterDoubleAnimationFrame(nudgeSpotlightRemeasure)
  }, delayMs)
}

function runTourStartWhenNotCancelled(cancelled: boolean, onReady: () => void): void {
  if (!cancelled) onReady()
}

function scheduleTourStartAfterPaint(
  isCancelled: () => boolean,
  onReady: () => void,
): number {
  return globalThis.requestAnimationFrame(() => {
    runAfterDoubleAnimationFrame(() => runTourStartWhenNotCancelled(isCancelled(), onReady))
  })
}

/**
 * Sensemaker first-load tooltip tour. Outside clicks do not dismiss; Escape skips (same as Skip Tour).
 */
export function SensemakerFtuxTour({ steps, shouldRun }: SensemakerFtuxTourProps) {
  const [run, setRun] = useState(false)
  const patchedRef = useRef(false)
  const remeasureTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(
    undefined,
  )
  const mutation = usePatchSensemakerFtux()

  const persistSkipped = useCallback(async () => {
    if (patchedRef.current) return
    patchedRef.current = true
    try {
      await mutation.mutateAsync('SKIPPED')
    } catch {
      patchedRef.current = false
    }
  }, [mutation])

  const persistCompleted = useCallback(async () => {
    if (patchedRef.current) return
    patchedRef.current = true
    try {
      await mutation.mutateAsync('COMPLETED')
    } catch {
      patchedRef.current = false
    }
  }, [mutation])

  const scheduleSpotlightRemeasure = useCallback(() => {
    globalThis.clearTimeout(remeasureTimerRef.current)
    remeasureTimerRef.current = scheduleDelayedSpotlightRemeasure(
      SPOTLIGHT_REMEASURE_DELAY_MS,
    )
  }, [])

  useEffect(() => {
    if (!shouldRun || steps.length === 0) return
    let cancelled = false
    let pollTimer: ReturnType<typeof globalThis.setTimeout> | undefined
    let rafId = 0

    const startWhenTargetReady = () => {
      if (cancelled) return
      const firstTarget = resolveStepTarget(steps[0]?.target)
      if (!firstTarget) {
        pollTimer = globalThis.setTimeout(startWhenTargetReady, 50)
        return
      }
      rafId = scheduleTourStartAfterPaint(() => cancelled, () => setRun(true))
    }

    startWhenTargetReady()

    return () => {
      cancelled = true
      if (pollTimer !== undefined) globalThis.clearTimeout(pollTimer)
      globalThis.cancelAnimationFrame(rafId)
    }
  }, [shouldRun, steps])

  useEffect(() => {
    if (!run) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setRun(false)
        void persistSkipped()
      }
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [run, persistSkipped])

  useEffect(
    () => () => {
      globalThis.clearTimeout(remeasureTimerRef.current)
    },
    [],
  )

  const handleEvent: EventHandler = useCallback(
    (data) => {
      if (data.action === ACTIONS.SKIP) {
        void persistSkipped()
        setRun(false)
        return
      }

      if (data.type === EVENTS.SCROLL_END || data.type === EVENTS.TOOLTIP) {
        scheduleSpotlightRemeasure()
      }

      if (data.type === EVENTS.TOUR_END) {
        if (data.status === STATUS.SKIPPED) {
          void persistSkipped()
        } else if (data.status === STATUS.FINISHED) {
          void persistCompleted()
        }
        setRun(false)
      }
    },
    [persistCompleted, persistSkipped, scheduleSpotlightRemeasure],
  )

  if (steps.length === 0) {
    return null
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      locale={{
        skip: 'Skip Tour',
        last: 'Done',
        next: 'Next',
        nextWithProgress: 'Next ({current} of {total})',
      }}
      styles={sensemakerFtuxJoyrideStyles}
      floatingOptions={{
        /** Figma `4154:4475`: no caret between tooltip and dashboard card */
        hideArrow: true,
        /** Keep tooltip in viewport space — matches fixed overlay + AppShell nested scroll. */
        strategy: 'fixed',
      }}
      options={{
        overlayClickAction: false,
        /** Figma `4154:4367`: primary stays “Next”; step “n of m” is in the title row (`FtuxStepHeader`). */
        showProgress: false,
        zIndex: 13050,
        primaryColor: palettePrimaryMain,
        backgroundColor: '#ffffff',
        textColor: '#4c5357',
        overlayColor: 'rgba(0,0,0,0.5)',
        buttons: ['skip', 'primary'],
        /** No extra halo around the target — cutout matches the metric card bounds (`4154:4475`). */
        spotlightPadding: 0,
        /** Match metric cards / Figma `rounded-[10px]` on spotlight hole */
        spotlightRadius: 10,
        offset: FTUX_STEP_TARGET_GAP_PX,
      }}
    />
  )
}
